import React from 'react';
import ReactDOM from 'react-dom/client';
import './styles/globals.css';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import AuthProvider from './components/AuthProvider';
import { Button, ConfigProvider, Result } from 'antd';
import customTheme from './styles/theme.json';
import ProtectedRoute from './components/ProtectedRoute';
import App from './pages/App';
import Dashboard from './pages/dashboard';
import LoginPage from './pages/login/LoginPage';
import StockLevelPage from './pages/reports/stock-level';
import MaterialIssuancePage from './pages/reports/material-issuance';
import VendorPerformancePage from './pages/reports/vendor-performance';
import MaterialStatusPage from './pages/reports/material-status';
import PurchaseOrderPage from './pages/purchase-order';
import PurchaseOrderInfoPage from './pages/purchase-order/info';
import PurchaseOrderFormCreatePage from './pages/purchase-order/form/create';
import PurchaseOrderFormEditPage from './pages/purchase-order/form/edit';
import DeliveryReceiptPage from './pages/stock-movement/delivery-receipt';
import DeliveryReceiptInfoPage from './pages/stock-movement/delivery-receipt/info';
import CanvassPage from './pages/purchase-canvass';
import ApprovalsPage from './pages/approvals';
import TrackingPage from './pages/tracking';
import SupplierDeliveriesPage from './pages/stock-movement/supplier-deliveries';
import CanvassInfoPage from './pages/purchase-canvass/info';
import PurchaseRequestPage from './pages/purchase-request/pr';
import PurchaseRequestInfoPage from './pages/purchase-request/pr/info';
import PurchaseRequestCreatePage from './pages/purchase-request/pr/form/create';
import PurchaseRequestFormEditPage from './pages/purchase-request/pr/form/edit';
import PurchaseRequestDetailPage from './pages/purchase-request/detail';
import BillOfMaterialPage from './pages/production/bill-of-material/bom';
import BillOfMaterialInfoPage from './pages/production/bill-of-material/bom/info';
import BillOfMaterialForm from './pages/production/bill-of-material/bom/form';
import BillOfMaterialDetail from './pages/production/bill-of-material/detail';
import SectionPage from './pages/production/section';
import LinePage from './pages/production/line';
import PlanningPage from './pages/production/planning';
import MaterialRequestPage from './pages/stock-movement/material-request';
import MaterialRequestInfoPage from './pages/stock-movement/material-request/info';
import MaterialRequestFormEditPage from './pages/stock-movement/material-request/form/edit';
import MaterialRequestFormCreatePage from './pages/stock-movement/material-request/form/create';
import ReceivingReportPage from './pages/stock-movement/receiving-report';
import ReceivingReportFormCreatePage from './pages/stock-movement/receiving-report/form/create';
import ReceivingReportEditForm from './pages/stock-movement/receiving-report/form/edit';
import ReceivingReportInfoPage from './pages/stock-movement/receiving-report/info';
import ProductPage from './pages/products/product';
import BrandPage from './pages/products/brands';
import CategoryPage from './pages/products/categories';
import UnitOfMeasurePage from './pages/products/unit-of-measure';
import VendorPage from './pages/partners/vendor';
import BuyerPage from './pages/partners/buyer';
import LineOfBusinessPage from './pages/partners/line-of-business';
import WarehousePage from './pages/warehouse-location/warehouse';
import LocationPage from './pages/warehouse-location/location';
import DepartmentPage from './pages/support-data/department';
import PositionPage from './pages/support-data/position';
import ModulePage from './pages/support-data/module';
import CompaniesPage from './pages/admin/companies';
import SequenceTypesPage from './pages/admin/sequence-types';
import SecurityGroupsPage from './pages/admin/security-groups';
import SecurityModuleKeysPage from './pages/admin/security-module-keys';
import NumberSeriesPage from './pages/admin/number-series';
import IrValuesPage from './pages/admin/ir-values';
import UserPage from './pages/user';
import UserFormUpdate from './pages/user/UserFormUpdate';
import UserFormCreate from './pages/user/UserFormCreate';

const queryClient = new QueryClient();

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement,
);
root.render(
  <React.StrictMode>
    <QueryClientProvider client={queryClient}>
      <ConfigProvider theme={customTheme}>
        <AuthProvider>
          <BrowserRouter basename={'/mmd'}>
            <Routes>
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <App />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="/report">
                  <Route
                    path="/report/stock-level"
                    element={<StockLevelPage />}
                  />
                  <Route
                    path="/report/material-issuance"
                    element={<MaterialIssuancePage />}
                  />
                  <Route
                    path="/report/vendor-performance"
                    element={<VendorPerformancePage />}
                  />
                  <Route
                    path="/report/material-status"
                    element={<MaterialStatusPage />}
                  />
                </Route>
                <Route path={'/purchase-order'}>
                  <Route
                    path="/purchase-order"
                    element={<PurchaseOrderPage />}
                  />
                  <Route
                    path="/purchase-order/:id"
                    element={<PurchaseOrderInfoPage />}
                  />
                  <Route
                    path="/purchase-order/request"
                    element={<PurchaseOrderFormCreatePage />}
                  />
                  <Route
                    path="/purchase-order/request/:id"
                    element={<PurchaseOrderFormEditPage />}
                  />
                </Route>
                <Route path={'/stock-movement/delivery-receipt'}>
                  <Route
                    path="/stock-movement/delivery-receipt"
                    element={<DeliveryReceiptPage />}
                  />
                  <Route
                    path="/stock-movement/delivery-receipt/:id"
                    element={<DeliveryReceiptInfoPage />}
                  />
                </Route>
                <Route path="/approvals" element={<ApprovalsPage />} />
                <Route path="/tracking" element={<TrackingPage />} />
                <Route path={'/purchase-canvass'}>
                  <Route path="/purchase-canvass" element={<CanvassPage />} />
                  <Route
                    path="/purchase-canvass/:id"
                    element={<CanvassInfoPage />}
                  />
                </Route>
                <Route path={'/purchase-request'}>
                  <Route
                    path="/purchase-request"
                    element={<PurchaseRequestPage />}
                  />
                  <Route
                    path="/purchase-request/:id"
                    element={<PurchaseRequestInfoPage />}
                  />
                  <Route
                    path="/purchase-request/request"
                    element={<PurchaseRequestCreatePage />}
                  />
                  <Route
                    path="/purchase-request/request/:id"
                    element={<PurchaseRequestFormEditPage />}
                  />
                  <Route
                    path="/purchase-request/detail"
                    element={<PurchaseRequestDetailPage />}
                  />
                </Route>
                <Route path={'/production'}>
                  <Route path="/production/bill-of-material">
                    <Route
                      path="/production/bill-of-material"
                      element={<BillOfMaterialPage />}
                    />
                    <Route
                      path="/production/bill-of-material/:id"
                      element={<BillOfMaterialInfoPage />}
                    />
                    <Route
                      path="/production/bill-of-material/request/:id"
                      element={<BillOfMaterialForm />}
                    />
                    <Route
                      path="/production/bill-of-material/detail"
                      element={<BillOfMaterialDetail />}
                    />
                  </Route>
                  <Route path="/production/section" element={<SectionPage />} />
                  <Route path="/production/line" element={<LinePage />} />
                  <Route
                    path="/production/planning"
                    element={<PlanningPage />}
                  />
                  <Route
                    path="/production/planning/:tab"
                    element={<PlanningPage />}
                  />
                  {/* the three pages became tabs of Planning; old links land on their tab */}
                  <Route
                    path="/production/planning-board"
                    element={
                      <Navigate to="/production/planning/schedule" replace />
                    }
                  />
                  <Route
                    path="/production/proder-progress"
                    element={
                      <Navigate to="/production/planning/proders" replace />
                    }
                  />
                  <Route
                    path="/production/planning-inputs"
                    element={
                      <Navigate to="/production/planning/setup" replace />
                    }
                  />
                </Route>
                <Route path="/stock-movement">
                  <Route
                    path="/stock-movement/supplier-deliveries"
                    element={<SupplierDeliveriesPage />}
                  />
                  <Route path="/stock-movement/material-request">
                    <Route
                      path="/stock-movement/material-request"
                      element={<MaterialRequestPage />}
                    />
                    <Route
                      path="/stock-movement/material-request/:id"
                      element={<MaterialRequestInfoPage />}
                    />
                    <Route
                      path="/stock-movement/material-request/request"
                      element={<MaterialRequestFormCreatePage />}
                    />
                    <Route
                      path="/stock-movement/material-request/request/:id"
                      element={<MaterialRequestFormEditPage />}
                    />
                  </Route>
                  <Route path={'/stock-movement/receiving-report'}>
                    <Route
                      path="/stock-movement/receiving-report"
                      element={<ReceivingReportPage />}
                    />
                    <Route
                      path="/stock-movement/receiving-report/:id"
                      element={<ReceivingReportInfoPage />}
                    />
                    <Route
                      path="/stock-movement/receiving-report/request"
                      element={<ReceivingReportFormCreatePage />}
                    />
                    <Route
                      path="/stock-movement/receiving-report/request/:id"
                      element={<ReceivingReportEditForm />}
                    />
                  </Route>
                </Route>
                <Route path={'/product'}>
                  <Route path="/product/product" element={<ProductPage />} />
                  <Route path="/product/brand" element={<BrandPage />} />
                  <Route path="/product/category" element={<CategoryPage />} />
                  <Route
                    path="/product/unit-of-measure"
                    element={<UnitOfMeasurePage />}
                  />
                </Route>
                <Route path={'/partner'}>
                  <Route path="/partner/vendors" element={<VendorPage />} />
                  <Route path="/partner/buyers" element={<BuyerPage />} />
                  <Route
                    path="/partner/line-of-business"
                    element={<LineOfBusinessPage />}
                  />
                </Route>
                <Route path={'/warehouse-location'}>
                  <Route
                    path="/warehouse-location/warehouse"
                    element={<WarehousePage />}
                  />
                  <Route
                    path="/warehouse-location/location"
                    element={<LocationPage />}
                  />
                </Route>
                <Route path={'/support-data'}>
                  <Route
                    path="/support-data/department"
                    element={<DepartmentPage />}
                  />
                  <Route
                    path="/support-data/position"
                    element={<PositionPage />}
                  />
                  <Route path="/support-data/module" element={<ModulePage />} />
                </Route>
                <Route path={'/admin'}>
                  <Route path="/admin/companies" element={<CompaniesPage />} />
                  <Route
                    path="/admin/number-series"
                    element={<NumberSeriesPage />}
                  />
                  <Route
                    path="/admin/sequence-types"
                    element={<SequenceTypesPage />}
                  />
                  <Route
                    path="/admin/security-groups"
                    element={<SecurityGroupsPage />}
                  />
                  <Route
                    path="/admin/security-module-keys"
                    element={<SecurityModuleKeysPage />}
                  />
                  <Route path="/admin/ir-values" element={<IrValuesPage />} />
                </Route>
                <Route path={'user'}>
                  <Route path="/user" element={<UserPage />} />
                  <Route path="/user/:id" element={<UserFormUpdate />} />
                  <Route path="/user/create" element={<UserFormCreate />} />
                </Route>
                <Route
                  path="*"
                  element={
                    <Result
                      status="404"
                      title="404"
                      subTitle="Sorry, the page you visited does not exist."
                      extra={
                        <Button type="primary" href={'/mmd'}>
                          Back Home
                        </Button>
                      }
                    />
                  }
                />
              </Route>
              <Route path="/login" element={<LoginPage />} />
            </Routes>
          </BrowserRouter>
        </AuthProvider>
      </ConfigProvider>
    </QueryClientProvider>
  </React.StrictMode>,
);
